import { faImage } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FC, Fragment, useRef, useState, useEffect } from "react";
import { t } from "i18next";

export type InputFileprops = {
  id: string;
  name: string;
  span?: string;
  spanButton: string;
  description: string;
  label: string;
  onFileChange?: (file: File | null) => void;
  selectedImage?: File | string | null; // Cambiar para aceptar File o string (URL)
  onRemoveImage?: () => void;
  tittleDelete: string;
};

export const InputFileLarge: FC<InputFileprops> = ({
  id,
  name,
  span,
  spanButton,
  description,
  label,
  onFileChange,
  selectedImage,
  onRemoveImage,
  tittleDelete,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);

  // Actualizar la previsualización cuando cambie selectedImage
  useEffect(() => {
    if (selectedImage instanceof File) {
      setImagePreviewUrl(URL.createObjectURL(selectedImage));
    } else if (typeof selectedImage === "string") {
      setImagePreviewUrl(selectedImage);
    } else {
      setImagePreviewUrl(null);
    }

    // Limpiar URL para evitar fugas de memoria
    return () => {
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
    };
  }, [selectedImage]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (onFileChange) onFileChange(file); // Notificar al componente padre
      setImagePreviewUrl(URL.createObjectURL(file)); // Crear previsualización
    }
    // Reinicia el valor del input para permitir subir el mismo archivo
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleRemoveImage = () => {
    if (onRemoveImage) onRemoveImage(); // Notificar al componente padre
    setImagePreviewUrl(null); // Limpiar la previsualización
  };

  return (
    <Fragment>
      <div>
        <div className="flex justify-between">
          <label
            htmlFor={id}
            className={`block text-sm font-medium leading-6 text-gray-900`}
          >
            {label}
          </label>
          <span className={`text-sm leading-6 text-gray-500`}>{span}</span>
        </div>
        <div className="mt-2 flex flex-col items-center h-full">
          <div
            className={`h-40 w-40 flex justify-center items-center border-dashed border rounded-lg border-gray-900/25 text-sm`}
            style={{
              backgroundImage: imagePreviewUrl ? `url(${imagePreviewUrl})` : "",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              backgroundSize: "contain",
            }}
          >
            <div className="text-center">
              {!imagePreviewUrl ? (
                <>
                  <div className="p-3">
                    <FontAwesomeIcon
                      icon={faImage}
                      className="h-10 w-10 text-gray-500"
                    />
                    <div className="mt-4 flex justify-center text-sm leading-6 text-gray-600">
                      <label
                        htmlFor={id}
                        className="relative cursor-pointer rounded-md bg-white font-semibold text-primary-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-primary-600 focus-within:ring-offset-2 hover:text-primary-500"
                      >
                        <span className="block text-center">{spanButton}</span>
                        <input
                          id={id}
                          name={name}
                          type="file"
                          accept=".png, .jpg, .jpeg"
                          className="sr-only"
                          onChange={handleFileChange}
                        />
                      </label>
                    </div>
                    <p className="text-xs leading-5 text-gray-600">
                      {description}
                    </p>
                  </div>
                </>
              ) : null}
            </div>
          </div>
          {imagePreviewUrl && (
            <div className={`flex justify-center items-center px-2 py-4`}>
              <div className="text-center">
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="mt-2 text-sm text-red-600 hover:text-red-800"
                >
                  {tittleDelete}
                </button>
                <label
                  htmlFor={id}
                  className="text-sm relative cursor-pointer rounded-md bg-white text-primary-600 hover:text-primary-500"
                >
                  <span className="block text-center">
                    {t("general.change_image")}
                  </span>
                  <input
                    id={id}
                    name={name}
                    type="file"
                    accept=".png, .jpg, .jpeg"
                    className="sr-only"
                    onChange={handleFileChange}
                  />
                </label>
              </div>
            </div>
          )}
        </div>
      </div>
    </Fragment>
  );
};
